// What the shell shows where, as data: the nav destinations (four or fewer tabs on a phone, the
// rest under More) and which chrome each screen keeps. Focus mode keeps none of it: only the card,
// its place in the round and the key hints.
import type { ComponentType } from "react";
import {
  Bookmark,
  BookOpen,
  EyeOff,
  FilePen,
  History,
  Inbox,
  Layers,
  ListChecks,
  ListTodo,
  Mails,
  MessageCircleQuestion,
  Newspaper,
  Settings,
  SlidersHorizontal,
  Sparkles,
  UserCheck,
} from "lucide-react";

type Icon = ComponentType<{ className?: string }>;

export interface NavItem {
  to:
    | "/feed"
    | "/inbox"
    | "/tasks"
    | "/saved"
    | "/work"
    | "/hidden"
    | "/recent"
    | "/screener"
    | "/accounts"
    | "/settings";
  label: string;
  icon: Icon;
  binding: string;
  /** Other paths that count as this item (thread pages are "Inbox", focus is "Feed"). */
  alsoActiveFor?: readonly string[];
  /** One of the phone's tabs; everything else opens from More. */
  phoneTab?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: "/feed",
    label: "Feed",
    icon: Layers,
    binding: "g f",
    alsoActiveFor: ["/focus", "/learning"],
    phoneTab: true,
  },
  {
    to: "/inbox",
    label: "Inbox",
    icon: Inbox,
    binding: "g i",
    alsoActiveFor: ["/t/"],
    phoneTab: true,
  },
  {
    to: "/tasks",
    label: "Tasks",
    icon: ListTodo,
    binding: "g t",
    alsoActiveFor: ["/brief"],
    phoneTab: true,
  },
  { to: "/saved", label: "Saved mail", icon: Bookmark, binding: "g v" },
  { to: "/work", label: "Under way", icon: ListChecks, binding: "g w", alsoActiveFor: ["/audit"] },
  { to: "/hidden", label: "Set aside", icon: EyeOff, binding: "g h" },
  {
    to: "/recent",
    label: "Recently done",
    icon: History,
    binding: "g r",
    alsoActiveFor: ["/fresh-start"],
  },
  { to: "/screener", label: "New senders", icon: UserCheck, binding: "g c" },
  { to: "/accounts", label: "Accounts", icon: Mails, binding: "g a" },
  { to: "/settings", label: "Settings", icon: Settings, binding: "g s" },
];

/**
 * Screens that wide screens reach from the feed header or with a key (Drafts is G then D, the
 * brief G then B); a phone has neither, so More lists them too.
 */
export const MORE_FEED_LINKS: readonly {
  to: "/learning" | "/feed/config" | "/answers" | "/ask" | "/compose" | "/brief";
  label: string;
  icon: Icon;
}[] = [
  { to: "/learning", label: "Learning", icon: Sparkles },
  { to: "/feed/config", label: "How Claude works", icon: SlidersHorizontal },
  { to: "/answers", label: "Saved answers", icon: BookOpen },
  { to: "/ask", label: "Ask about your mail", icon: MessageCircleQuestion },
  { to: "/compose", label: "Drafts", icon: FilePen },
  { to: "/brief", label: "Latest brief", icon: Newspaper },
];

export const PHONE_TABS = NAV_ITEMS.filter((item) => item.phoneTab);
export const PHONE_MORE = NAV_ITEMS.filter((item) => !item.phoneTab);

export function isNavActive(
  pathname: string,
  item: { to: string; alsoActiveFor?: readonly string[] | undefined },
): boolean {
  return (
    pathname === item.to ||
    pathname.startsWith(`${item.to}/`) ||
    (item.alsoActiveFor ?? []).some((p) => pathname.startsWith(p))
  );
}

/** Screens that show the Dock (the feed and everything that configures or explains it). */
const DOCK_PATHS = ["/feed", "/learning", "/work", "/hidden", "/audit"];

function under(pathname: string, paths: readonly string[]): boolean {
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export interface ShellChrome {
  sidebar: boolean;
  topBar: boolean;
  briefBanner: boolean;
  dock: boolean;
  bottomNav: boolean;
}

/**
 * The chrome around a screen. Focus mode (/focus) hides all of it (Esc leaves focus), and so does
 * the guided first run (/welcome), which has its own calm frame.
 */
export function shellChrome(pathname: string): ShellChrome {
  const focus = under(pathname, ["/focus", "/welcome"]);
  return {
    sidebar: !focus,
    topBar: !focus,
    briefBanner: !focus,
    dock: !focus && under(pathname, DOCK_PATHS),
    bottomNav: !focus,
  };
}
